import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { apiDelete, apiGet, apiPost, apiPut } from '../lib/api-client';
import { formatMoney, penceToPoundsInput, poundsToPence, supplierOrderVatPence } from '../lib/money';
import { apiError, sharePointLinks } from '../lib/utils';
import { editableStatus, stashDuplicate, takeDuplicate } from '../lib/duplicate';
import { loadedRecordKey, useEditGuard } from '../lib/dirty';
import { LineTable } from '../components/documents/LineTable';
import { enumLabel } from '../lib/labels';
import { InvoiceFormat2 } from '../components/documents/InvoiceFormat2';
import { InvoicePreview } from '../components/documents/InvoicePreview';
import { CreateInvoiceDialog } from '../components/records/CreateInvoiceDialog';
import { Button } from '../components/ui/Button';
import { Dialog } from '../components/ui/Dialog';
import { useNotice } from '../components/ui/Notice';
import { useLinkedPreview } from '../lib/linked-scroll';
import { RecordStepper } from '../components/records/RecordStepper';
import { PageHead, RecordLink, SectionCard } from '../components/ui/chrome';
import { Field, GrowingText, Input, Select } from '../components/ui/Input';
import { useOurContact } from '../lib/our-contact';
import { useTermsSeed } from '../lib/terms-template';
import { SupplierTemplateSelect, TermsTemplateSelect } from '../components/documents/TemplateSelects';
import { engagementChoices, useFreshRows } from '../lib/select-choices';
import { useAuth } from '../contexts/AuthContext';
import { EditWithAiButton, RecordAssistDialog } from '../components/records/RecordAssistDialog';

function orderCurrency(value: unknown): string {
  const code = String(value || 'GBP').toUpperCase();
  return code === 'EUR' || code === 'USD' ? code : 'GBP';
}

const INVOICE_SIDE_ORDER = ['draft', 'issued', 'paid', 'credited', 'void'];

export function invoiceSideGroups(invoices: { id: string; number: string; status: string }[]): { title: string; invoices: { id: string; number: string; status: string }[] }[] {
  const titles: Record<string, string> = { draft: 'Draft', issued: 'Issued', paid: 'Paid', credited: 'Credited', void: 'Void' };
  const buckets = new Map<string, { id: string; number: string; status: string }[]>();
  for (const invoice of invoices) {
    const status = titles[invoice.status] ? invoice.status : 'issued';
    const rows = buckets.get(status) || [];
    rows.push(invoice);
    buckets.set(status, rows);
  }
  return INVOICE_SIDE_ORDER.filter((status) => buckets.has(status)).map((status) => ({
    title: titles[status],
    invoices: buckets.get(status) || [],
  }));
}

function poLineLabel(reference: string, description: string): string {
  const left = reference.trim();
  const right = description.trim();
  if (left && right) return `${left} - ${right}`;
  return left || right;
}

function lineFromRow(line: Record<string, unknown>, keepId: boolean): { id: string; reference: string; description: string; qty: string; pounds: string } {
  const qtyValue = Number(line.qty);
  const qty = Number.isFinite(qtyValue) && qtyValue > 0 ? qtyValue : 1;
  const amount = Number(line.amountPence || 0);
  const unit = typeof line.unitPence === 'number' ? Number(line.unitPence) : Math.round(amount / qty);
  return {
    id: keepId ? String(line.id || '') : '',
    reference: String(line.reference || ''),
    description: String(line.description || ''),
    qty: String(qty),
    pounds: penceToPoundsInput(unit),
  };
}

function lineTotalPence(line: { qty: string; pounds: string }): number {
  const qty = Number(line.qty);
  const count = Number.isFinite(qty) && qty > 0 ? qty : 1;
  return Math.round(count * poundsToPence(line.pounds));
}

export function PurchaseOrderPage() {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const notice = useNotice();
  const { can } = useAuth();
  const location = useLocation();
  const supplier = location.pathname.startsWith('/supplier-purchase-orders');
  const [vendors, setVendors] = useState<{ id: string; name: string; postalAddress?: string; emails?: string[] }[]>([]);
  const { previewRef, onFormScroll } = useLinkedPreview();
  const [engagements, reloadEngagements] = useFreshRows<{ id: string; name: string; customerId?: string; customerCode?: string; customerName?: string }>('/api/engagements');
  const [vendorId, setVendorId] = useState('');
  const [docKind, setDocKind] = useState('services');
  const markCleanRef = useRef<() => void>(() => {});
  const [id, setId] = useState('');
  const [engagementId, setEngagementId] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [valuePounds, setValuePounds] = useState('');
  const [invoicedNetPence, setInvoicedNet] = useState<number | null>(null);
  const [invoicedVatPence, setInvoicedVat] = useState<number | null>(null);
  const [remainingPence, setRemaining] = useState<number | null>(null);
  const [orderNetPence, setOrderNet] = useState<number | null>(null);
  const [orderVatPence, setOrderVat] = useState<number | null>(null);
  const [orderTotalPence, setOrderTotal] = useState<number | null>(null);
  const [remainingVatPence, setRemainingVat] = useState<number | null>(null);
  const [remainingTotalPence, setRemainingTotal] = useState<number | null>(null);
  const [vatLabel, setVatLabel] = useState('');
  const [currency, setCurrency] = useState('GBP');
  const [issuedOn, setIssuedOn] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [status, setStatus] = useState('open');
  const [storedStatus, setStoredStatus] = useState('');
  const [billingModel, setBillingModel] = useState('fixed_price');
  const [orderLines, setOrderLines] = useState<{ id: string; reference: string; description: string; qty: string; pounds: string }[]>([]);
  const [layout, setLayout] = useState('');
  const [billToName, setBillToName] = useState('');
  const [billToAddress, setBillToAddress] = useState('');
  const [billToEmail, setBillToEmail] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactRole, setContactRole] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [delivery, setDelivery] = useState('');
  const [termsNotice, setTermsNotice] = useState('');
  const [acceptance, setAcceptance] = useState('');
  const [sourceFileName, setSourceFileName] = useState('');
  const [sharePointUrl, setSharePointUrl] = useState('');
  const [sharePointFiles, setSharePointFiles] = useState<{ name: string; url: string }[]>([]);
  const [quotationId, setQuotationId] = useState('');
  const [quotationNumber, setQuotationNumber] = useState('');
  const [linkedCharges, setLinkedCharges] = useState<{ id: string; name: string }[]>([]);
  const [linkedInvoices, setLinkedInvoices] = useState<{ id: string; number: string; status: string }[]>([]);
  const [linkedPaymentsIn, setLinkedPaymentsIn] = useState<{ id: string; invoiceId: string; invoiceNumber: string; amountPence: number; paidOn: string; currency: string }[]>([]);
  const [linkedBills, setLinkedBills] = useState<{ id: string; number: string }[]>([]);
  const [linkedPaymentsOut, setLinkedPaymentsOut] = useState<{ id: string; billId: string; billNumber: string; amountPence: number; paidOn: string; currency: string }[]>([]);
  const [vatIncluded, setVatIncluded] = useState<boolean | undefined>(undefined);
  const [supplierVat, setSupplierVat] = useState<'none' | 'standard'>('none');
  const [unsavedCopy, setUnsavedCopy] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [askInvoice, setAskInvoice] = useState(false);
  const [askDelete, setAskDelete] = useState(false);
  const [assist, setAssist] = useState<'create' | 'edit' | null>(null);
  const poNumberTouched = useRef(false);
  const duplicateApplied = useRef(false);
  useOurContact(supplier && (!routeId || routeId === 'new'), (contact) => {
    setContactName((value) => value || contact.contactName);
    setContactRole((value) => value || contact.contactRole);
    setContactEmail((value) => value || contact.contactEmail);
    setContactPhone((value) => value || contact.contactPhone);
  });
  useEffect(() => {
    if (!id || searchParams.get('createInvoice') !== '1') return;
    setAskInvoice(true);
    const next = new URLSearchParams(searchParams);
    next.delete('createInvoice');
    setSearchParams(next, { replace: true });
  }, [id, searchParams, setSearchParams]);
  useTermsSeed('purchase_order', supplier && (!routeId || routeId === 'new') && !location.search.includes('duplicate=1'), (row) => {
    setPaymentTerms((value) => value || row.paymentTerms);
    setDelivery((value) => value || row.delivery);
    setTermsNotice((value) => value || row.notice);
    setAcceptance((value) => value || row.acceptance);
  });

  useEffect(() => {
    void apiGet<{ id: string; name: string; postalAddress?: string; emails?: string[] }[]>('/api/vendors').then(setVendors);
    if (!routeId || routeId === 'new') {
      if (duplicateApplied.current) return;
      const params = new URLSearchParams(location.search);
      if (params.get('duplicate') !== '1') {
        if (!supplier) setOrderLines([{ id: '', reference: '1', description: 'Order total', qty: '1', pounds: '' }]);
        return;
      }
      const copy = takeDuplicate('purchase-order');
      if (!copy || (copy.direction === 'supplier') !== supplier) return;
      duplicateApplied.current = true;
      setUnsavedCopy(true);
      setId('');
      setEngagementId(String(copy.engagementId || ''));
      setVendorId(String(copy.vendorId || ''));
      setDocKind(String(copy.docKind || 'services'));
      setPoNumber(String(copy.poNumber || ''));
      poNumberTouched.current = false;
      setValuePounds(penceToPoundsInput(copy.valuePence));
      setCurrency(orderCurrency(copy.currency));
      setIssuedOn(String(copy.issuedOn || ''));
      setExpiresOn(String(copy.expiresOn || ''));
      setStatus(editableStatus(copy.status, 'open'));
      setStoredStatus('');
      setBillingModel(String(copy.billingModel || 'fixed_price'));
      setLayout(copy.layout === '1' || copy.layout === '2' ? String(copy.layout) : '');
      setBillToName(String(copy.billToName || ''));
      setBillToAddress(String(copy.billToAddress || ''));
      setBillToEmail(String(copy.billToEmail || ''));
      setContactName(String(copy.contactName || ''));
      setContactRole(String(copy.contactRole || ''));
      setContactEmail(String(copy.contactEmail || ''));
      setContactPhone(String(copy.contactPhone || ''));
      setPaymentTerms(String(copy.paymentTerms || ''));
      setDelivery(String(copy.delivery || ''));
      setTermsNotice(String(copy.notice || ''));
      setAcceptance(String(copy.acceptance || ''));
      setVatIncluded(copy.vatIncluded === true ? true : copy.vatIncluded === false ? false : undefined);
      setSupplierVat(copy.supplierVat === 'standard' ? 'standard' : 'none');
      setSourceFileName(String(copy.sourceFileName || ''));
      setSharePointUrl(String(copy.sharePointUrl || ''));
      setSharePointFiles(sharePointLinks(copy));
      setQuotationId('');
      setQuotationNumber('');
      setLinkedCharges([]);
      setLinkedInvoices([]);
      setLinkedPaymentsIn([]);
      setLinkedBills([]);
      setLinkedPaymentsOut([]);
      const copiedLines = ((copy.lines as Record<string, unknown>[]) || []).map((line) => lineFromRow(line, false));
      setOrderLines(!supplier && !copiedLines.length ? [{ id: '', reference: '1', description: 'Order total', qty: '1', pounds: penceToPoundsInput(copy.valuePence) }] : copiedLines);
      return;
    }
    void apiGet<Record<string, unknown>[]>(supplier ? '/api/purchase-orders?direction=supplier' : '/api/purchase-orders').then((list) => {
      const row = list.find((item) => item.id === routeId);
      if (!row) return;
      setId(String(row.id));
      setEngagementId(String(row.engagementId || ''));
      setVendorId(String(row.vendorId || ''));
      setDocKind(String(row.docKind || 'services'));
      setPoNumber(String(row.poNumber || ''));
      poNumberTouched.current = true;
      setValuePounds(penceToPoundsInput(row.valuePence));
      setInvoicedNet(row.invoicedNetPence == null ? null : Number(row.invoicedNetPence));
      setInvoicedVat(row.invoicedVatPence == null ? null : Number(row.invoicedVatPence));
      setRemaining(row.remainingPence == null ? null : Number(row.remainingPence));
      setOrderNet(row.orderNetPence == null ? null : Number(row.orderNetPence));
      setOrderVat(row.orderVatPence == null ? null : Number(row.orderVatPence));
      setOrderTotal(row.orderTotalPence == null ? null : Number(row.orderTotalPence));
      setRemainingVat(row.remainingVatPence == null ? null : Number(row.remainingVatPence));
      setRemainingTotal(row.remainingTotalPence == null ? null : Number(row.remainingTotalPence));
      setVatLabel(String(row.vatLabel || ''));
      setSupplierVat(row.supplierVat === 'standard' ? 'standard' : 'none');
      setCurrency(orderCurrency(row.currency));
      setIssuedOn(String(row.issuedOn || ''));
      setExpiresOn(String(row.expiresOn || ''));
      setStatus(String(row.status || 'open'));
      setStoredStatus(String(row.status || 'open'));
      setBillingModel(String(row.billingModel || 'fixed_price'));
      setLayout(row.layout === '1' || row.layout === '2' ? String(row.layout) : '');
      setBillToName(String(row.billToName || ''));
      setBillToAddress(String(row.billToAddress || ''));
      setBillToEmail(String(row.billToEmail || ''));
      setContactName(String(row.contactName || ''));
      setContactRole(String(row.contactRole || ''));
      setContactEmail(String(row.contactEmail || ''));
      setContactPhone(String(row.contactPhone || ''));
      setPaymentTerms(String(row.paymentTerms || ''));
      setDelivery(String(row.delivery || ''));
      setTermsNotice(String(row.notice || ''));
      setAcceptance(String(row.acceptance || ''));
      setSourceFileName(String(row.sourceFileName || ''));
      setSharePointUrl(String(row.sharePointUrl || ''));
      setSharePointFiles(sharePointLinks(row));
      setQuotationId(String(row.quotationId || ''));
      setQuotationNumber(String(row.quotationNumber || ''));
      setLinkedCharges(((row.chargeLines as { id?: string; name?: string }[]) || []).map((line) => ({
        id: String(line.id || ''),
        name: String(line.name || 'Charge line'),
      })).filter((line) => line.id));
      setLinkedInvoices(((row.invoices as { id?: string; number?: string; internalNumber?: string; status?: string }[]) || []).map((inv) => ({
        id: String(inv.id || ''),
        number: String(inv.internalNumber || inv.number || 'Invoice'),
        status: String(inv.status || ''),
      })).filter((inv) => inv.id));
      setLinkedPaymentsIn(((row.paymentsIn as { id?: string; invoiceId?: string; invoiceNumber?: string; amountPence?: number; paidOn?: string; currency?: string }[]) || []).map((pay) => ({
        id: String(pay.id || ''),
        invoiceId: String(pay.invoiceId || ''),
        invoiceNumber: String(pay.invoiceNumber || ''),
        amountPence: Number(pay.amountPence || 0),
        paidOn: String(pay.paidOn || ''),
        currency: String(pay.currency || row.currency || 'GBP'),
      })).filter((pay) => pay.id));
      setLinkedBills(((row.bills as { id?: string; number?: string }[]) || []).map((bill) => ({
        id: String(bill.id || ''),
        number: String(bill.number || 'Bill'),
      })).filter((bill) => bill.id));
      setLinkedPaymentsOut(((row.paymentsOut as { id?: string; billId?: string; billNumber?: string; amountPence?: number; paidOn?: string; currency?: string }[]) || []).map((pay) => ({
        id: String(pay.id || ''),
        billId: String(pay.billId || ''),
        billNumber: String(pay.billNumber || ''),
        amountPence: Number(pay.amountPence || 0),
        paidOn: String(pay.paidOn || ''),
        currency: String(pay.currency || row.currency || 'GBP'),
      })).filter((pay) => pay.id));
      const loadedLines = ((row.lines as Record<string, unknown>[]) || []).map((line) => lineFromRow(line, true));
      setOrderLines(!supplier && !loadedLines.length ? [{ id: '', reference: '1', description: 'Order total', qty: '1', pounds: penceToPoundsInput(row.valuePence) }] : loadedLines);
    });
  }, [routeId]);

  function shown(pence: number | null) {
    return pence == null ? 'not available yet' : formatMoney(pence, currency || 'GBP');
  }

  const locked = supplier && storedStatus === 'issued';

  function orderBody() {
    const lines = orderLines.filter((line) => line.reference || line.pounds).map((line) => {
      const qty = Number(line.qty);
      const count = Number.isFinite(qty) && qty > 0 ? qty : 1;
      const unitPence = poundsToPence(line.pounds);
      return {
        id: line.id || undefined,
        reference: line.reference,
        description: line.description,
        qty: count,
        unitPence,
        amountPence: Math.round(count * unitPence),
      };
    });
    const safeStatus = status === 'issued' ? (storedStatus || 'open') : status;
    const shared = {
      poNumber,
      valuePence: lines.length ? lines.reduce((sum, line) => sum + line.amountPence, 0) : poundsToPence(valuePounds),
      currency,
      issuedOn,
      expiresOn,
      status: safeStatus,
      lines,
      ...(layout === '1' || layout === '2' ? { layout } : {}),
      ...(typeof vatIncluded === 'boolean' ? { vatIncluded } : {}),
      billToName, billToAddress, billToEmail,
      ...(supplier ? { supplierVat, contactName, contactRole, contactEmail, contactPhone, paymentTerms, delivery, notice: termsNotice, acceptance } : {}),
    };
    return supplier
      ? { direction: 'supplier', vendorId, docKind, billingModel: 'fixed_price', ...shared }
      : { direction: 'customer', engagementId, billingModel, ...shared };
  }

  async function save() {
    if (locked) return;
    try {
      const row = id ? await apiPut<{ id: string }>(`/api/purchase-orders/${id}`, orderBody()) : await apiPost<{ id: string }>('/api/purchase-orders', orderBody());
      markCleanRef.current();
      navigate(`${supplier ? '/supplier-purchase-orders' : '/purchase-orders'}/${row.id}`);
      notice.success('Saved');
    } catch (err) {
      notice.failure(apiError(err, 'Save failed'));
    }
  }

  function duplicateOrder() {
    const lines = orderLines.filter((line) => line.reference || line.pounds).map((line) => {
      const qty = Number(line.qty);
      const count = Number.isFinite(qty) && qty > 0 ? qty : 1;
      const unitPence = poundsToPence(line.pounds);
      return {
        reference: line.reference,
        description: line.description,
        qty: count,
        unitPence,
        amountPence: Math.round(count * unitPence),
      };
    });
    stashDuplicate('purchase-order', {
      direction: supplier ? 'supplier' : 'customer',
      vendorId,
      docKind,
      engagementId,
      poNumber: '',
      valuePence: lines.length ? lines.reduce((sum, line) => sum + line.amountPence, 0) : poundsToPence(valuePounds),
      currency,
      issuedOn,
      expiresOn,
      status: editableStatus(status, 'open'),
      billingModel,
      ...(layout === '1' || layout === '2' ? { layout } : {}),
      ...(typeof vatIncluded === 'boolean' ? { vatIncluded } : {}),
      supplierVat,
      billToName, billToAddress, billToEmail, contactName, contactRole, contactEmail, contactPhone, paymentTerms, delivery, notice: termsNotice, acceptance,
      sharePointUrl,
      sourceFileName,
      lines,
    });
    navigate(`${supplier ? '/supplier-purchase-orders' : '/purchase-orders'}/new?duplicate=1`);
  }

  async function removeOrder() {
    if (!id) return;
    await notice.run('Deleting', async () => {
      await apiDelete(`/api/purchase-orders/${id}`);
      navigate(supplier ? '/supplier-purchase-orders' : '/purchase-orders');
    }, 'Deleted');
  }

  useEffect(() => {
    if (!supplier || id || poNumberTouched.current || !vendorId) return;
    const handle = window.setTimeout(() => {
      void apiGet<{ number: string }>(`/api/purchase-orders/next-number?vendorId=${encodeURIComponent(vendorId)}&docKind=${encodeURIComponent(docKind)}`)
        .then((row) => { if (!poNumberTouched.current && row.number) setPoNumber(row.number); })
        .catch(() => undefined);
    }, 200);
    return () => window.clearTimeout(handle);
  }, [supplier, id, vendorId, docKind]);

  async function openReview() {
    if (locked) return;
    try {
      const saved = id ? await apiPut<{ id: string }>(`/api/purchase-orders/${id}`, orderBody()) : await apiPost<{ id: string }>('/api/purchase-orders', orderBody());
      markCleanRef.current();
      navigate(`${supplier ? '/supplier-purchase-orders' : '/purchase-orders'}/${saved.id}/review`);
      notice.success('Saved');
    } catch (err) {
      notice.failure(apiError(err, 'Save failed'));
    }
  }

  markCleanRef.current = useEditGuard(loadedRecordKey(routeId, routeId === 'new' ? undefined : id || undefined), JSON.stringify({ vendorId, docKind, engagementId, poNumber, valuePounds, currency, issuedOn, expiresOn, status, billingModel, layout, supplierVat, orderLines, billToName, billToAddress, billToEmail, contactName, contactRole, contactEmail, contactPhone, paymentTerms, delivery, termsNotice, acceptance }), save);

  const linesEditor = (
    <SectionCard title="Order lines" hint="Each line has a reference, a description, a quantity, and a unit price. The line total is quantity times that price.">
      <LineTable
        columns={['Reference', 'Description', 'Qty', 'Unit price', 'Line total']}
        count={orderLines.length}
        locked={locked}
        addLabel="Add line item"
        onAdd={() => setOrderLines((rows) => [...rows, { id: '', reference: '', description: '', qty: '1', pounds: '' }])}
        onRemove={(index) => setOrderLines((rows) => rows.filter((_, i) => i !== index))}
        render={(index) => {
          const line = orderLines[index];
          return [
            <Input value={line.reference} onChange={(event) => setOrderLines((rows) => rows.map((item, i) => i === index ? { ...item, reference: event.target.value } : item))} />,
            <Input value={line.description} onChange={(event) => setOrderLines((rows) => rows.map((item, i) => i === index ? { ...item, description: event.target.value } : item))} />,
            <Input type="number" step="0.25" value={line.qty} onChange={(event) => setOrderLines((rows) => rows.map((item, i) => i === index ? { ...item, qty: event.target.value } : item))} />,
            <Input type="number" step="0.01" value={line.pounds} onChange={(event) => setOrderLines((rows) => rows.map((item, i) => i === index ? { ...item, pounds: event.target.value } : item))} />,
            <span className="tabular-nums">{formatMoney(lineTotalPence(line), currency || 'GBP')}</span>,
          ];
        }}
      />
      <div className="mt-3 text-right text-sm">Total {formatMoney(orderLines.reduce((sum, line) => sum + lineTotalPence(line), 0), currency || 'GBP')}</div>
    </SectionCard>
  );

  const livePence = orderLines.reduce((sum, line) => sum + lineTotalPence(line), 0);
  const previewLines = orderLines.filter((line) => line.reference || line.pounds).map((line) => ({
    qty: Number(line.qty) > 0 ? Number(line.qty) : 1,
    description: poLineLabel(line.reference, line.description),
    unitPence: poundsToPence(line.pounds),
    amountPence: lineTotalPence(line),
  }));
  const previewVat = supplier ? supplierOrderVatPence(livePence, supplierVat) : null;

  return (
    <div>
      {assist ? <RecordAssistDialog kind={supplier ? 'supplier-order' : 'customer-order'} mode={assist} recordId={id || undefined} current={orderBody()} title={supplier ? 'Purchase order' : 'Customer order'} onClose={() => setAssist(null)} onSaved={(saved) => { const next = String(saved.id || ''); const path = `${supplier ? '/supplier-purchase-orders' : '/purchase-orders'}/${next}`; if (next && next !== routeId) navigate(path); else window.location.assign(path); }} /> : null}
      {unsavedCopy ? <p className="mb-3 text-sm text-[var(--bo-muted)]">Not saved yet. Save creates a new order.</p> : null}
      <PageHead
        stepper={routeId ? <RecordStepper listUrl={supplier ? '/api/purchase-orders?direction=supplier' : '/api/purchase-orders'} id={routeId} basePath={supplier ? '/supplier-purchase-orders' : '/purchase-orders'} defaultFilter={supplier ? 'all' : 'unspent'} /> : null}
        crumbs={[{ label: supplier ? 'Purchase orders' : 'Customer orders', to: supplier ? '/supplier-purchase-orders' : '/purchase-orders' }, { label: poNumber || 'New' }]}
        back={{ label: supplier ? 'Back to purchase orders' : 'Back to customer orders', to: supplier ? '/supplier-purchase-orders' : '/purchase-orders' }}
        title={poNumber || (supplier ? 'Purchase order' : 'Customer order')}
        subtitle={supplier ? 'Supplier order, lines, and the source file.' : 'Customer order lines and the file it was imported from.'}
        actions={(
          <>
            {id ? <EditWithAiButton kind={supplier ? 'supplier-order' : 'customer-order'} status={storedStatus || status} onClick={() => setAssist('edit')} /> : <Button variant="outline" onClick={() => setAssist('create')}>Prepare with AI</Button>}
            {locked ? null : <Button variant="outline" onClick={() => void save()}>Save</Button>}
            {!locked && !supplier && id && can('invoices', 'draft') ? <Button onClick={() => setAskInvoice(true)}>Create invoice</Button> : null}
            {!locked && supplier && id ? <Button onClick={() => void openReview()}>Review & issue</Button> : null}
            {!locked && id ? <Button variant="outline" onClick={duplicateOrder}>Duplicate</Button> : null}
            {id && can('purchase_orders', 'edit') ? <Button variant="destructive" onClick={() => setAskDelete(true)}>Delete</Button> : null}
          </>
        )}
      />
      <div className={`grid gap-4 ${supplier ? 'h-[calc(100vh-16rem)] items-stretch xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]' : id || linkedInvoices.length ? 'items-start xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]' : 'items-start'}`}>
      <div className={supplier ? 'min-h-0 overflow-y-auto pr-1' : ''} onScroll={supplier ? onFormScroll : undefined}>
      <fieldset disabled={locked} className="m-0 min-w-0 space-y-4 border-0 p-0">
      {id ? (
        <SectionCard title="Position" hint={`VAT: ${vatLabel || 'not stated'}. Net values remain the basis for available order value.`}>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead><tr className="border-b text-left text-[var(--bo-muted)]"><th className="py-2">Position</th><th className="py-2 text-right">Net</th><th className="py-2 text-right">VAT</th><th className="py-2 text-right">Total incl. VAT</th></tr></thead>
              <tbody>
                <tr className="border-b"><th className="py-2 text-left font-medium">Order</th><td className="py-2 text-right">{shown(orderNetPence)}</td><td className="py-2 text-right">{shown(orderVatPence)}</td><td className="py-2 text-right">{shown(orderTotalPence)}</td></tr>
                <tr className="border-b"><th className="py-2 text-left font-medium">Invoiced</th><td className="py-2 text-right">{shown(invoicedNetPence)}</td><td className="py-2 text-right">{shown(invoicedVatPence)}</td><td className="py-2 text-right">{shown(invoicedNetPence == null || invoicedVatPence == null ? null : invoicedNetPence + invoicedVatPence)}</td></tr>
                <tr><th className="py-2 text-left font-medium">Remaining</th><td className="py-2 text-right">{shown(remainingPence)}</td><td className="py-2 text-right">{shown(remainingVatPence)}</td><td className="py-2 text-right">{shown(remainingTotalPence)}</td></tr>
              </tbody>
            </table>
          </div>
        </SectionCard>
      ) : null}
      <SectionCard title="Order" hint={supplier ? 'The number can be edited until the first save.' : 'The customer follows the engagement. The number is the one on their document.'}>
        <div className="grid gap-3 lg:grid-cols-2 xl:grid-cols-3">
          {supplier ? (
            <>
              <Field label="Supplier"><Select searchable value={vendorId} onChange={(event) => {
                const next = event.target.value;
                setVendorId(next);
                const vendor = vendors.find((row) => row.id === next);
                if (!vendor) return;
                setBillToName(vendor.name || '');
                setBillToAddress(vendor.postalAddress || '');
                setBillToEmail(vendor.emails?.[0] || '');
              }}><option value="">Select</option>{vendors.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}</Select></Field>
              <Field label="Kind"><Select value={docKind} onChange={(event) => setDocKind(event.target.value)}><option value="services">Services</option><option value="licence">Licences</option><option value="support">Support and cloud</option><option value="commercial">Commercial</option></Select></Field>
            </>
          ) : (
            <>
              <Field label="Customer"><Input readOnly value={engagements.find((row) => row.id === engagementId)?.customerName || ''} /></Field>
              <Field label="Engagement"><Select searchable choices={engagementChoices(engagements)} onOpen={reloadEngagements} value={engagementId} onChange={(event) => setEngagementId(event.target.value)} /></Field>
            </>
          )}
          <Field label="PO number"><Input value={poNumber} disabled={Boolean(id)} onChange={(event) => { poNumberTouched.current = true; setPoNumber(event.target.value); }} /></Field>
          <Field label="Value"><Input readOnly value={orderLines.some((line) => line.pounds) ? (orderLines.reduce((sum, line) => sum + lineTotalPence(line), 0) / 100).toFixed(2) : valuePounds} /></Field>
          <Field label="Currency"><Select value={orderCurrency(currency)} onChange={(event) => setCurrency(event.target.value)}><option value="GBP">GBP</option><option value="EUR">EUR</option><option value="USD">USD</option></Select></Field>
          <Field label="Issued"><Input type="date" value={issuedOn} onChange={(event) => setIssuedOn(event.target.value)} /></Field>
          <Field label="Expires"><Input type="date" value={expiresOn} onChange={(event) => setExpiresOn(event.target.value)} /><span className="mt-1 block text-xs text-[var(--bo-muted)]">Leave blank if the order does not expire.</span></Field>
          {supplier ? null : <Field label="Billing"><Select value={billingModel} onChange={(event) => setBillingModel(event.target.value)}><option value="fixed_price">Fixed price</option><option value="time_and_materials">Time and materials</option></Select></Field>}
          {supplier ? <Field label="Format"><Select value={layout || '2'} onChange={(event) => setLayout(event.target.value === '1' ? '1' : '2')}><option value="2">Modern</option><option value="1">Traditional</option></Select></Field> : null}
          {supplier ? <Field label="VAT"><Select value={supplierVat} onChange={(event) => setSupplierVat(event.target.value === 'standard' ? 'standard' : 'none')}><option value="none">No VAT</option><option value="standard">VAT 20% on top</option></Select><span className="mt-1 block text-xs text-[var(--bo-muted)]">No VAT keeps the total as the line net, in GBP, EUR, or USD. VAT 20% adds UK VAT on top of that net.</span></Field> : null}
          <Field label="Status">{locked ? <Input value={enumLabel(status || 'issued')} readOnly /> : <Select value={status} onChange={(event) => setStatus(event.target.value)}>{['draft', 'open', 'expired', 'exhausted', 'cancelled', 'void'].map((item) => <option key={item} value={item}>{enumLabel(item)}</option>)}</Select>}</Field>
        </div>
      </SectionCard>
      <div className="grid gap-4 lg:grid-cols-2">
        <SectionCard title={supplier ? 'Supplier' : 'Client Details'} hint={supplier ? 'Who we are ordering from. Their invoice should quote this purchase order.' : 'Who the order is addressed to.'}>
          <div className="space-y-3">
            <Field label={supplier ? 'Supplier name' : 'Bill to'}><Input value={billToName} onChange={(event) => setBillToName(event.target.value)} /></Field>
            <Field label="Address"><GrowingText value={billToAddress} onChange={setBillToAddress} /></Field>
            <Field label="Email"><Input value={billToEmail} onChange={(event) => setBillToEmail(event.target.value)} /></Field>
          </div>
        </SectionCard>
        {supplier ? (
          <SectionCard title="Bill to" hint="Maxiron is the buyer. The supplier invoices this company. Bank details are not printed.">
            <div className="space-y-3">
              <p className="text-sm leading-relaxed">Maxiron Ltd<br />3rd Floor 86-90 Paul Street, London, EC2A 4NE<br />Company No. 14444817 · VAT No. 435737379<br />office@maxiron.com</p>
              <SupplierTemplateSelect onApply={(contact) => { setContactName(contact.contactName); setContactRole(contact.contactRole); setContactEmail(contact.contactEmail); setContactPhone(contact.contactPhone); }} />
              <Field label="Our contact"><Input value={contactName} onChange={(event) => setContactName(event.target.value)} /></Field>
              <Field label="Role"><Input value={contactRole} onChange={(event) => setContactRole(event.target.value)} /></Field>
              <Field label="Email"><Input value={contactEmail} onChange={(event) => setContactEmail(event.target.value)} /></Field>
              <Field label="Telephone"><Input value={contactPhone} onChange={(event) => setContactPhone(event.target.value)} /></Field>
            </div>
          </SectionCard>
        ) : (
          <SectionCard title="Source" hint="The file this order was recorded from.">
            <p className="text-sm text-[var(--bo-muted)]"><SourceFiles files={sharePointFiles} /></p>
          </SectionCard>
        )}
      </div>
      {supplier ? (
        <SectionCard title="Commercial terms" hint="Printed on the order only when the field has text.">
          <TermsTemplateSelect kind="purchase_order" onApply={(row) => { setPaymentTerms(row.paymentTerms); setDelivery(row.delivery); setTermsNotice(row.notice); setAcceptance(row.acceptance); }} />
          <div className="grid gap-3 lg:grid-cols-2">
            <Field label="Payment terms"><GrowingText value={paymentTerms} onChange={setPaymentTerms} /></Field>
            <Field label="Delivery"><GrowingText value={delivery} onChange={setDelivery} /></Field>
            <Field label="Notice"><GrowingText value={termsNotice} onChange={setTermsNotice} /></Field>
            <Field label="To proceed"><GrowingText value={acceptance} onChange={setAcceptance} /></Field>
          </div>
        </SectionCard>
      ) : null}
      {linesEditor}
      </fieldset>
      </div>
      {id || linkedInvoices.length || supplier ? (
        <div ref={supplier ? previewRef : undefined} className={`min-w-0 space-y-4 ${supplier ? 'min-h-0 overflow-y-auto' : ''}`}>
          {id && !supplier && quotationId ? (
            <SectionCard title="Sales offer" hint="The offer this order came from.">
              <RecordLink to={`/quotations/${quotationId}`} backLabel={`Back to ${poNumber || 'order'}`}>{quotationNumber || 'Sales offer'}</RecordLink>
            </SectionCard>
          ) : null}
          {id && !supplier ? (
            <SectionCard title="Charge lines" hint="Charge lines recorded against this order.">
              <div className="flex flex-col gap-2">
                <RecordLink to={`/charge-lines?poId=${encodeURIComponent(id)}&poNumber=${encodeURIComponent(poNumber)}`} backLabel={`Back to ${poNumber || 'order'}`}>Open charge lines</RecordLink>
                {linkedCharges.map((line) => (
                  <RecordLink key={line.id} to={`/charge-lines?poId=${encodeURIComponent(id)}&poNumber=${encodeURIComponent(poNumber)}&record=${encodeURIComponent(line.id)}`} backLabel={`Back to ${poNumber || 'order'}`}>{line.name}</RecordLink>
                ))}
                {linkedCharges.length ? null : <p className="text-sm text-[var(--bo-muted)]">No charge lines on this order.</p>}
              </div>
            </SectionCard>
          ) : null}
          {id && !supplier ? (
            <SectionCard title="Invoices" hint="Invoices whose lines use a line on this order.">
              <div className="flex flex-col gap-3">
                <RecordLink to={`/invoices?poId=${encodeURIComponent(id)}&poNumber=${encodeURIComponent(poNumber)}&filter=all`} backLabel={`Back to ${poNumber || 'order'}`}>Open invoices</RecordLink>
                {invoiceSideGroups(linkedInvoices).map((group) => (
                  <div key={group.title} className="flex flex-col gap-2">
                    <div className="text-xs text-gray-500 dark:text-gray-400">{group.title}</div>
                    {group.invoices.map((inv) => (
                      <RecordLink key={inv.id} to={`/invoices/${inv.id}`} backLabel={`Back to ${poNumber || 'order'}`}>{inv.number}</RecordLink>
                    ))}
                  </div>
                ))}
                {linkedInvoices.length ? null : <p className="text-sm text-[var(--bo-muted)]">No invoices on this order.</p>}
              </div>
            </SectionCard>
          ) : null}
          {id && !supplier ? (
            <SectionCard title="Payments in" hint="Payments recorded against those invoices.">
              <div className="flex flex-col gap-2">
                <RecordLink to={`/payments-in?poId=${encodeURIComponent(id)}&poNumber=${encodeURIComponent(poNumber)}`} backLabel={`Back to ${poNumber || 'order'}`}>Open payments in</RecordLink>
                {linkedPaymentsIn.map((pay) => (
                  <RecordLink key={pay.id} to={`/payments-in?poId=${encodeURIComponent(id)}&poNumber=${encodeURIComponent(poNumber)}&record=${encodeURIComponent(pay.id)}`} backLabel={`Back to ${poNumber || 'order'}`}>{pay.invoiceNumber || 'Payment'} · {formatMoney(pay.amountPence, pay.currency)} · {pay.paidOn}</RecordLink>
                ))}
                {linkedPaymentsIn.length ? null : <p className="text-sm text-[var(--bo-muted)]">No payments in on this order.</p>}
              </div>
            </SectionCard>
          ) : null}
          {id && supplier ? (
            <SectionCard title="Bills" hint="Bills recorded against this purchase order.">
              <div className="flex flex-col gap-2">
                <RecordLink to={`/bills?poId=${encodeURIComponent(id)}&poNumber=${encodeURIComponent(poNumber)}`} backLabel={`Back to ${poNumber || 'order'}`}>Open bills</RecordLink>
                {linkedBills.map((bill) => (
                  <RecordLink key={bill.id} to={`/bills/${bill.id}`} backLabel={`Back to ${poNumber || 'order'}`}>{bill.number}</RecordLink>
                ))}
              </div>
            </SectionCard>
          ) : null}
          {id && supplier ? (
            <SectionCard title="Payments out" hint="Payments recorded against those bills.">
              <div className="flex flex-col gap-2">
                <RecordLink to={`/payments-out?poId=${encodeURIComponent(id)}&poNumber=${encodeURIComponent(poNumber)}`} backLabel={`Back to ${poNumber || 'order'}`}>Open payments out</RecordLink>
                {linkedPaymentsOut.map((pay) => (
                  <RecordLink key={pay.id} to={pay.billId ? `/payments-out?billId=${encodeURIComponent(pay.billId)}&billNumber=${encodeURIComponent(pay.billNumber)}` : `/payments-out?poId=${encodeURIComponent(id)}&poNumber=${encodeURIComponent(poNumber)}`} backLabel={`Back to ${poNumber || 'order'}`}>{pay.billNumber || 'Payment'} · {formatMoney(pay.amountPence, pay.currency)} · {pay.paidOn}</RecordLink>
                ))}
              </div>
            </SectionCard>
          ) : null}
          {supplier ? (
            <div className="space-y-3 rounded-lg bg-[var(--bo-preview)] p-3">
              {layout === '1' ? (
                <InvoicePreview documentTitle="PURCHASE ORDER" numberLabel="PO NO." number={poNumber || undefined} customerName={vendors.find((row) => row.id === vendorId)?.name || ''} billingEmails={[]} billToName={billToName} billToAddress={billToAddress} billToEmail={billToEmail} issuedOn={issuedOn} dueOn="" currency={currency || 'GBP'} poNumber={poNumber} contactName={contactName} contactRole={contactRole} contactEmail={contactEmail} contactPhone={contactPhone} paymentTerms={paymentTerms} delivery={delivery} notice={termsNotice} acceptance={acceptance} vatPence={previewVat} lines={previewLines} />
              ) : (
                <InvoiceFormat2 documentTitle="PURCHASE ORDER" number={poNumber || undefined} customerName={vendors.find((row) => row.id === vendorId)?.name || ''} billingEmails={[]} billToName={billToName} billToAddress={billToAddress} billToEmail={billToEmail} issuedOn={issuedOn} dueOn="" currency={currency || 'GBP'} poNumber="" contactName={contactName} contactRole={contactRole} contactEmail={contactEmail} contactPhone={contactPhone} paymentTerms={paymentTerms} delivery={delivery} notice={termsNotice} acceptance={acceptance} vatPence={previewVat} lines={previewLines} />
              )}
              <p className="text-sm text-[var(--bo-muted)]"><SourceFiles files={sharePointFiles} /></p>
            </div>
          ) : null}
        </div>
      ) : null}
      </div>
      {askInvoice && id ? (
        <CreateInvoiceDialog
          purchaseOrderId={id}
          engagementId={engagementId}
          currency={currency || 'GBP'}
          open={askInvoice}
          onClose={() => setAskInvoice(false)}
          onCreated={(invoiceId) => {
            setAskInvoice(false);
            navigate(`/invoices/${invoiceId}`, { state: { back: { label: `Back to ${poNumber || 'order'}`, to: `/purchase-orders/${id}` } } });
          }}
        />
      ) : null}
      {askDelete ? (
        <Dialog title="Delete order" onClose={() => setAskDelete(false)} footer={<><Button variant="outline" onClick={() => setAskDelete(false)}>Cancel</Button><Button variant="destructive" onClick={() => void removeOrder()}>Delete</Button></>}>
          <p className="text-sm text-gray-600 dark:text-gray-300">Delete this order? This cannot be undone.</p>
        </Dialog>
      ) : null}
    </div>
  );
}

function SourceFiles({ files }: { files: { name: string; url: string }[] }) {
  if (!files.length) return <>No source file on this order.</>;
  return (
    <span className="inline-flex flex-wrap gap-x-3 gap-y-1">
      {files.map((file) => (
        <a key={file.url} className="text-[var(--bo-link)] underline" href={file.url} target="_blank" rel="noreferrer">{file.name}</a>
      ))}
    </span>
  );
}
